import React, { useState, useEffect } from 'react';
import CobraSpinner from '../shared/CobraSpinner';
import {
    Box,
    Container,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    VStack,
    HStack,
    Select,
    Badge,
    Text,
    Flex,
    SimpleGrid,
    Tooltip,
    Icon,
    useDisclosure,
    Divider
} from '@chakra-ui/react';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiUserPlus, FiUsers, FiClock, FiCheckCircle } from 'react-icons/fi';
import registrationService from '../services/registrationService';
import niveauService from '../services/niveauService';
import AddUserModal from '../shared/AddUserModal';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

const RegistrationManagement = () => {
    const [registrations, setRegistrations] = useState([]);
    const [niveaux, setNiveaux] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRegistration, setSelectedRegistration] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
    const [userModalReg, setUserModalReg] = useState(null);
    const toast = useToast();

    // Theme Colors
    const primaryText = '#2C3E50';
    const brandYellow = '#F2C919';

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const fetchData = async () => {
        try {
            const [regResponse, nivResponse] = await Promise.all([
                registrationService.getRegistrations(statusFilter ? { status: statusFilter } : {}),
                niveauService.getAllNiveaux()
            ]);
            setRegistrations(regResponse.data);
            setNiveaux(nivResponse.data);
        } catch (error) {
            toast({
                title: 'خطأ في تحميل البيانات',
                status: 'error',
                duration: 3000,
                position: 'top-left'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, newStatus, notes) => {
        try {
            await registrationService.updateRegistrationStatus(id, { status: newStatus, notes });
            toast({
                title: 'تم تحديث الحالة بنجاح',
                status: 'success',
                duration: 3000,
                position: 'top-left'
            });
            fetchData();
            onClose();
        } catch (error) {
            toast({
                title: 'خطأ في التحديث',
                status: 'error',
                duration: 3000,
                position: 'top-left'
            });
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.')) {
            try {
                await registrationService.deleteRegistration(id);
                toast({
                    title: 'تم الحذف بنجاح',
                    status: 'success',
                    duration: 3000,
                    position: 'top-left'
                });
                fetchData();
            } catch (error) {
                toast({
                    title: 'خطأ في الحذف',
                    status: 'error',
                    duration: 3000,
                    position: 'top-left'
                });
            }
        }
    };

    const getStatusStyle = (status) => {
        const styles = {
            pending: { bg: 'yellow.100', color: 'yellow.800' },
            contacted: { bg: 'blue.100', color: 'blue.800' },
            enrolled: { bg: 'green.100', color: 'green.800' },
            rejected: { bg: 'red.100', color: 'red.800' }
        };
        return styles[status] || { bg: 'gray.100', color: 'gray.800' };
    };

    const getStatusText = (status) => {
        const texts = {
            pending: 'قيد الانتظار',
            contacted: 'تم التواصل',
            enrolled: 'مسجل',
            rejected: 'مرفوض'
        };
        return texts[status] || status;
    };

    // Derived stats
    const totalRequests = registrations.length;
    const pendingRequests = registrations.filter(r => r.status === 'pending').length;
    const enrolledRequests = registrations.filter(r => r.status === 'enrolled').length;

    if (loading) {
        return (
            <Flex minH="100vh" align="center" justify="center" bg="gray.50" dir="rtl">
                <CobraSpinner size="xl" color={brandYellow} thickness="4px" />
            </Flex>
        );
    }

    return (
        <Box dir="rtl" bg="gray.50" minH="100vh" pb={12}>
            <Container maxW="1600px" py={10}>
                
                {/* Page Header */}
                <Flex justify="space-between" align="center" mb={10} wrap="wrap" gap={4}>
                    <VStack align="start" spacing={2}>
                        <Heading size="2xl" color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="900">
                            إدارة طلبات التسجيل
                        </Heading>
                        <Text color="gray.500" fontSize="lg" fontFamily="Tajawal, sans-serif" fontWeight="600">
                            متابعة وتحديث حالات طلبات التسجيل الواردة من المنصة.
                        </Text>
                    </VStack>
                    
                    <Select
                        w={{ base: '100%', md: '250px' }}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        fontFamily="Tajawal, sans-serif"
                        bg="white"
                        size="lg"
                        borderRadius="xl"
                        borderColor="gray.200"
                        boxShadow="sm"
                        fontWeight="700"
                        color={primaryText}
                    >
                        <option value="">جميع الحالات</option>
                        <option value="pending">قيد الانتظار</option>
                        <option value="contacted">تم التواصل</option>
                        <option value="enrolled">مسجل</option>
                        <option value="rejected">مرفوض</option>
                    </Select>
                </Flex>

                {/* Quick Stats Grid */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mb={12}>
                    <MotionBox
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Box p={8} bg="white" borderRadius="3xl" boxShadow="0 10px 30px rgba(0,0,0,0.05)" border="1px solid" borderColor="gray.100">
                            <HStack justify="space-between">
                                <VStack align="start" spacing={1}>
                                    <Text color="gray.500" fontSize="lg" fontFamily="Tajawal, sans-serif" fontWeight="700">
                                        إجمالي الطلبات
                                    </Text>
                                    <Heading size="2xl" color={primaryText} fontFamily="Tajawal, sans-serif">{totalRequests}</Heading>
                                </VStack>
                                <Box p={4} bg="blue.50" borderRadius="2xl">
                                    <Icon as={FiUsers} boxSize={8} color="blue.500" />
                                </Box>
                            </HStack>
                        </Box>
                    </MotionBox>

                    <MotionBox
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                    >
                        <Box p={8} bg="white" borderRadius="3xl" boxShadow="0 10px 30px rgba(0,0,0,0.05)" border="1px solid" borderColor="gray.100">
                            <HStack justify="space-between">
                                <VStack align="start" spacing={1}>
                                    <Text color="gray.500" fontSize="lg" fontFamily="Tajawal, sans-serif" fontWeight="700">
                                        قيد الانتظار
                                    </Text>
                                    <Heading size="2xl" color="yellow.500" fontFamily="Tajawal, sans-serif">{pendingRequests}</Heading>
                                </VStack>
                                <Box p={4} bg="yellow.50" borderRadius="2xl">
                                    <Icon as={FiClock} boxSize={8} color="yellow.500" />
                                </Box>
                            </HStack>
                        </Box>
                    </MotionBox>

                    <MotionBox
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.2 }}
                    >
                        <Box p={8} bg="white" borderRadius="3xl" boxShadow="0 10px 30px rgba(0,0,0,0.05)" border="1px solid" borderColor="gray.100">
                            <HStack justify="space-between">
                                <VStack align="start" spacing={1}>
                                    <Text color="gray.500" fontSize="lg" fontFamily="Tajawal, sans-serif" fontWeight="700">
                                        تم التسجيل
                                    </Text>
                                    <Heading size="2xl" color="green.500" fontFamily="Tajawal, sans-serif">{enrolledRequests}</Heading>
                                </VStack>
                                <Box p={4} bg="green.50" borderRadius="2xl">
                                    <Icon as={FiCheckCircle} boxSize={8} color="green.500" />
                                </Box>
                            </HStack>
                        </Box>
                    </MotionBox>
                </SimpleGrid>

                {/* Table Section */}
                <MotionBox
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                >
                    <Box bg="white" borderRadius="3xl" boxShadow="0 10px 30px rgba(0,0,0,0.05)" overflow="hidden" border="1px solid" borderColor="gray.100">
                        <Box overflowX="auto">
                            <Table variant="simple" size="md">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="800" py={6} fontSize="sm">اسم الطالب</Th>
                                        <Th fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="800" py={6} fontSize="sm">رقم الهاتف</Th>
                                        <Th fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="800" py={6} fontSize="sm">المستوى</Th>
                                        <Th fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="800" py={6} fontSize="sm">الباقة</Th>
                                        <Th fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="800" py={6} fontSize="sm">الحالة</Th>
                                        <Th fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="800" py={6} fontSize="sm">تاريخ الطلب</Th>
                                        <Th fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="800" py={6} fontSize="sm">إجراءات</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {registrations.map((reg) => {
                                        const statusStyle = getStatusStyle(reg.status);
                                        return (
                                            <Tr key={reg._id} _hover={{ bg: 'gray.50' }} transition="all 0.2s">
                                                <Td py={5}>
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontFamily="Tajawal, sans-serif" fontWeight="800" color={primaryText} fontSize="md">
                                                            {reg.studentName}
                                                        </Text>
                                                        <Text fontFamily="Tajawal, sans-serif" color="gray.500" fontSize="sm">
                                                            ولي الأمر: {reg.parentName}
                                                        </Text>
                                                    </VStack>
                                                </Td>
                                                <Td py={5}>
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontFamily="Tajawal, sans-serif" fontWeight="700" color={primaryText} dir="ltr">
                                                            {reg.parentPhone}
                                                        </Text>
                                                        <Text fontFamily="Tajawal, sans-serif" color="gray.500" fontSize="xs">
                                                            {reg.email}
                                                        </Text>
                                                    </VStack>
                                                </Td>
                                                <Td py={5}>
                                                    <Badge 
                                                        bg="purple.50" 
                                                        color="purple.600" 
                                                        fontFamily="Tajawal, sans-serif" 
                                                        px={3} py={1} 
                                                        borderRadius="lg"
                                                    >
                                                        {reg.niveau?.name || 'غير محدد'}
                                                    </Badge>
                                                </Td>
                                                <Td py={5}>
                                                    <Badge 
                                                        bg="orange.50" 
                                                        color="orange.600" 
                                                        fontFamily="Tajawal, sans-serif" 
                                                        px={3} py={1} 
                                                        borderRadius="lg"
                                                        maxW="200px"
                                                        isTruncated
                                                    >
                                                        {reg.pack?.nameAr || reg.pack?.name || 'غير محدد'}
                                                    </Badge>
                                                </Td>
                                                <Td py={5}>
                                                    <Badge 
                                                        bg={statusStyle.bg} 
                                                        color={statusStyle.color} 
                                                        fontFamily="Tajawal, sans-serif" 
                                                        px={3} py={1} 
                                                        borderRadius="lg"
                                                        fontWeight="800"
                                                    >
                                                        {getStatusText(reg.status)}
                                                    </Badge>
                                                </Td>
                                                <Td py={5} fontFamily="Tajawal, sans-serif" color="gray.600" fontWeight="600" fontSize="sm">
                                                    {new Date(reg.createdAt).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric' })}
                                                </Td>
                                                <Td py={5}>
                                                    <HStack spacing={3}>
                                                        <Tooltip label="تحديث الحالة والملاحظات" hasArrow placement="top" fontFamily="Tajawal, sans-serif">
                                                            <IconButton
                                                                icon={<FiEdit />}
                                                                size="md"
                                                                colorScheme="blue"
                                                                variant="ghost"
                                                                borderRadius="xl"
                                                                onClick={() => {
                                                                    setSelectedRegistration(reg);
                                                                    onOpen();
                                                                }}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="إنشاء حساب للتلميذ" hasArrow placement="top" fontFamily="Tajawal, sans-serif">
                                                            <IconButton
                                                                icon={<FiUserPlus />}
                                                                size="md"
                                                                colorScheme="green"
                                                                variant="ghost"
                                                                borderRadius="xl"
                                                                isDisabled={reg.status === 'enrolled'}
                                                                onClick={() => {
                                                                    setUserModalReg(reg);
                                                                    onUserModalOpen();
                                                                }}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="حذف الطلب" hasArrow placement="top" fontFamily="Tajawal, sans-serif" bg="red.500">
                                                            <IconButton
                                                                icon={<FiTrash2 />}
                                                                size="md"
                                                                colorScheme="red"
                                                                variant="ghost"
                                                                borderRadius="xl"
                                                                onClick={() => handleDelete(reg._id)}
                                                            />
                                                        </Tooltip>
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>

                            {registrations.length === 0 && (
                                <Box textAlign="center" py={20}>
                                    <Icon as={FiUsers} boxSize={16} color="gray.300" mb={4} />
                                    <Text fontSize="xl" color="gray.500" fontFamily="Tajawal, sans-serif" fontWeight="700">
                                        لا توجد طلبات تسجيل في الوقت الحالي
                                    </Text>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </MotionBox>

                {/* Modals */}
                <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
                    <ModalOverlay backdropFilter="blur(5px)" />
                    <ModalContent dir="rtl" borderRadius="2xl" p={2}>
                        <ModalHeader fontFamily="Tajawal, sans-serif" color={primaryText} fontWeight="900" fontSize="2xl">
                            تحديث حالة الطلب
                        </ModalHeader>
                        <ModalCloseButton left="16px" right="unset" top="16px" />
                        <Divider borderColor="gray.100" mb={4} />
                        <ModalBody>
                            <VStack spacing={6}>
                                <FormControl>
                                    <FormLabel fontFamily="Tajawal, sans-serif" color="gray.600" fontWeight="700">الحالة الجديدة</FormLabel>
                                    <Select
                                        size="lg"
                                        borderRadius="xl"
                                        value={selectedRegistration?.status}
                                        onChange={(e) => setSelectedRegistration({
                                            ...selectedRegistration,
                                            status: e.target.value
                                        })}
                                        fontFamily="Tajawal, sans-serif"
                                        fontWeight="600"
                                    >
                                        <option value="pending">قيد الانتظار</option>
                                        <option value="contacted">تم التواصل</option>
                                        <option value="enrolled">مسجل</option>
                                        <option value="rejected">مرفوض</option>
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontFamily="Tajawal, sans-serif" color="gray.600" fontWeight="700">ملاحظات الإدارة</FormLabel>
                                    <Input
                                        size="lg"
                                        borderRadius="xl"
                                        value={selectedRegistration?.notes || ''}
                                        onChange={(e) => setSelectedRegistration({
                                            ...selectedRegistration,
                                            notes: e.target.value
                                        })}
                                        placeholder="مثال: تم الاتصال بولي الأمر..."
                                        fontFamily="Tajawal, sans-serif"
                                    />
                                </FormControl>
                            </VStack>
                        </ModalBody>
                        <ModalFooter mt={4}>
                            <Button variant="ghost" ml={3} onClick={onClose} fontFamily="Tajawal, sans-serif" size="lg" borderRadius="xl">
                                إلغاء
                            </Button>
                            <Button
                                bg={primaryText}
                                color="white"
                                _hover={{ bg: 'black' }}
                                size="lg"
                                borderRadius="xl"
                                px={8}
                                onClick={() => handleUpdateStatus(
                                    selectedRegistration._id,
                                    selectedRegistration.status,
                                    selectedRegistration.notes
                                )}
                                fontFamily="Tajawal, sans-serif"
                            >
                                حفظ التغييرات
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {isUserModalOpen && (
                    <AddUserModal
                        isOpen={isUserModalOpen}
                        onClose={onUserModalClose}
                        initialData={userModalReg}
                        registrationId={userModalReg?._id}
                        onSuccess={fetchData}
                    />
                )}
            </Container>
        </Box>
    );
};

export default RegistrationManagement;
